import { Link } from '@tanstack/react-router';
import {
  formatActivity,
  type ActivityEntry,
  type ActivitySubjectRef,
} from '@/lib/format-activity';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

export interface ActivityFeedProps {
  workspaceSlug: string;
  entries: ReadonlyArray<ActivityEntry>;
  isLoading?: boolean;
  /**
   * `compact` removes the section's outer chrome — useful when
   * embedded in a page that already provides its own framing.
   * Default (`false`) wraps the feed in the standard "Section"
   * header treatment so it can stand on its own.
   */
  compact?: boolean;
  emptyLabel?: string;
  /** Optional footer slot — used by callers to render a
   *  `Load more` button for paginated feeds. */
  footer?: React.ReactNode;
  className?: string;
  title?: string;
}

/**
 * Glass redesign — Phase 7a.
 *
 * Rebuilt to match the activity-row pattern from TaskDetail's
 * `<ActivityEventRow>`: small `·` dot icon in a 22px circle, actor
 * + verb + subject + detail inline, relative time on the right.
 *
 * Outer chrome dropped — used to be `bg-card border p-6` which
 * read as a heavy modal-ish block. Now a quiet section header
 * (11px uppercase faint label) over a flat list of rows; matches
 * the `<Section>` rhythm established in TaskDetail.
 *
 * Used by: WorkspaceHomePage (recent activity), ProjectActivityPage.
 */
export const ActivityFeed = ({
  workspaceSlug,
  entries,
  isLoading = false,
  compact = false,
  emptyLabel = 'No activity yet.',
  footer,
  className,
  title = 'Activity',
}: ActivityFeedProps) => {
  const body = (
    <>
      {isLoading ? (
        <p className="text-foreground-faint px-1 py-1.5 text-[12.5px]">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-foreground-faint px-1 py-1.5 text-[12.5px]">{emptyLabel}</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {entries.map((entry) => (
            <ActivityRow
              key={entry.id}
              workspaceSlug={workspaceSlug}
              entry={entry}
            />
          ))}
        </div>
      )}
      {footer ? <div className="pt-3">{footer}</div> : null}
    </>
  );

  if (compact) {
    return <div className={cn(className)}>{body}</div>;
  }

  return (
    <section className={cn('mb-8', className)}>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-foreground-muted m-0 text-[11px] font-semibold tracking-[0.06em] uppercase">
          {title}
        </h3>
      </div>
      {body}
    </section>
  );
};

interface ActivityRowProps {
  workspaceSlug: string;
  entry: ActivityEntry;
}

/**
 * Activity row — same shape as TaskDetail's `<ActivityEventRow>`.
 * 22px secondary-bg `·` dot, actor + verb + subject inline,
 * relative time on the right.
 */
const ActivityRow = ({ workspaceSlug, entry }: ActivityRowProps) => {
  const attrs = entry.attributes ?? {};
  const formatted = formatActivity(entry);
  const actorName = attrs.actor_name ?? attrs.actor_email ?? 'Someone';

  return (
    <div className="text-foreground-muted flex items-center gap-2.5 px-1 py-1.5 text-[12px]">
      <span
        className={cn(
          'text-foreground-faint inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full',
          'bg-secondary',
        )}
      >
        ·
      </span>
      <span className="min-w-0 flex-1 leading-[1.5]">
        <strong className="text-foreground font-medium">{actorName}</strong>{' '}
        {formatted.verb}
        {formatted.subject ? (
          <>
            {' '}
            <SubjectLink workspaceSlug={workspaceSlug} subject={formatted.subject} />
          </>
        ) : null}
        {formatted.detail ? (
          <span className="text-foreground-faint">{formatted.detail}</span>
        ) : null}
      </span>
      <span className="text-foreground-faint flex-shrink-0 text-[11px]">
        {formatRelativeTime(attrs.created_at) || ''}
      </span>
    </div>
  );
};

const SubjectLink = ({
  workspaceSlug,
  subject,
}: {
  workspaceSlug: string;
  subject: ActivitySubjectRef;
}) => {
  const className =
    'text-foreground hover:text-primary font-medium underline-offset-2 hover:underline transition-colors';

  switch (subject.href.kind) {
    case 'task':
      return (
        <Link
          to="/w/$workspaceSlug/t/$taskUlid"
          params={{ workspaceSlug, taskUlid: subject.href.taskUlid }}
          className={className}
        >
          {subject.label}
        </Link>
      );
    case 'note':
      return (
        <Link
          to="/w/$workspaceSlug/n/$noteUlid"
          params={{ workspaceSlug, noteUlid: subject.href.noteUlid }}
          className={className}
        >
          {subject.label}
        </Link>
      );
    case 'project':
      return (
        <Link
          to="/w/$workspaceSlug/p/$projectSlug"
          params={{ workspaceSlug, projectSlug: subject.href.projectSlug }}
          className={className}
        >
          {subject.label}
        </Link>
      );
    case 'none':
    default:
      return <span className="text-foreground font-medium">{subject.label}</span>;
  }
};
